import { useState, useEffect } from 'react';
import '../css/Phase1Input.css';
import { fetchWithAuth } from '../config/api';
import { MOCK_MODE } from '../config/mock';
import EditableLabel from './EditableLabel';
import EditablePlaceholder from './EditablePlaceholder';
import { useLabels } from '../config/labelsContext';

interface Phase1InputProps {
  onComplete: (sessionId: string, userInput: string, title?: string) => void;
  username: string; // Cognito username
  showAlert?: (message: string, title?: string) => void;
}

const Phase1Input = ({ onComplete, username, showAlert }: Phase1InputProps) => {
  const { labels } = useLabels();

  // Use Cognito username as user_id
  const getUserId = (): string => {
    return username || 'anonymous';
  };

  // 4つの観点での入力
  const [usageField, setUsageField] = useState(''); // 利用分野
  const [technicalFeature, setTechnicalFeature] = useState(''); // 技術的特徴
  const [problem, setProblem] = useState(''); // 課題
  const [effect, setEffect] = useState(''); // 効果作用
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [charCount, setCharCount] = useState(0);

  // Initialize character count on mount
  useEffect(() => {
    const totalLength =
      usageField.length + technicalFeature.length + problem.length + effect.length;
    setCharCount(totalLength);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFieldChange = () => {
    const totalLength =
      usageField.length + technicalFeature.length + problem.length + effect.length;
    setCharCount(totalLength);
    if (totalLength > 2000) {
      setError(labels.phase1.errorTooLong);
    } else {
      setError('');
    }
  };

  const alertUI = (message: string) => {
    if (showAlert) return showAlert(message, labels.modals.noticeTitle);
    window.alert(message);
  };

  const extractErrorMessage = (detail: unknown, fallback: string): string => {
    if (typeof detail === 'string' && detail.trim()) return detail;
    if (Array.isArray(detail)) {
      const messages = detail
        .map((item) => {
          if (typeof item === 'string') return item;
          if (item && typeof item === 'object' && 'msg' in item) {
            return String((item as { msg?: unknown }).msg || '');
          }
          return '';
        })
        .filter(Boolean);
      if (messages.length > 0) return messages.join('\n');
    }
    if (detail && typeof detail === 'object') {
      const obj = detail as { message?: unknown; error?: unknown; detail?: unknown };
      return extractErrorMessage(obj.message || obj.error || obj.detail, fallback);
    }
    return fallback;
  };

  const validateInput = (): boolean => {
    const missingFields: string[] = [];
    if (!usageField.trim()) missingFields.push(labels.phase1.inputFieldUsage);
    if (!technicalFeature.trim()) missingFields.push(labels.phase1.inputFieldTech);
    if (!problem.trim()) missingFields.push(labels.phase1.inputFieldProblem);
    if (!effect.trim()) missingFields.push(labels.phase1.inputFieldEffect);

    if (missingFields.length > 0) {
      alertUI(`以下の項目を入力してください\n\n${missingFields.map((f) => `・${f}`).join('\n')}`);
      return false;
    }

    const totalLength =
      usageField.length + technicalFeature.length + problem.length + effect.length;
    if (totalLength > 2000) {
      alertUI(labels.phase1.errorTooLong);
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateInput()) {
      return;
    }

    setLoading(true);
    setError('');

    let mockSid: string | null = null;
    let mockInput: string | null = null;
    let mockTitle: string | null = null;

    try {
      // 4つの観点を結合
      const combinedInput = `${labels.phase1.inputFieldUsage}: ${usageField}\n${labels.phase1.inputFieldTech}: ${technicalFeature}\n${labels.phase1.inputFieldProblem}: ${problem}\n${labels.phase1.inputFieldEffect}: ${effect}`;

      if (MOCK_MODE) {
        mockSid = `SESSION-MOCK-${Date.now().toString(36)}`;
        mockInput = combinedInput;
        mockTitle = usageField.trim() || labels.phase1.defaultTitle;
      } else {
        // Step 1, 2, 3: Validate and start workflow
        const userId = getUserId();
        const response = await fetchWithAuth(`/api/phase1/start-workflow`, {
          method: 'POST',
          body: JSON.stringify({
            text: combinedInput,
            user_id: userId,
          }),
        });

        if (!response.ok) {
          // Backend may return plain text (e.g. Internal Server Error). Don't assume JSON.
          const ct = response.headers.get('content-type') || '';
          if (ct.includes('application/json')) {
            // FastAPI validation errors (422) can return `detail` as an array
            // of error objects, while other endpoints may return nested JSON.
            // Normalize all shapes before rendering the message.
            const errorData = (await response.json()) as { detail?: unknown };
            throw new Error(
              extractErrorMessage(errorData.detail, labels.phase1.errorWorkflowStart)
            );
          }
          const errorText = await response.text();
          throw new Error(errorText || labels.phase1.errorWorkflowStart);
        }

        const data = (await response.json()) as { session_id: string; title?: string };
        onComplete(data.session_id, combinedInput, data.title);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('ワークフロー開始に失敗しました:', err);
      setError(err instanceof Error ? err.message : labels.phase1.errorGeneric);
    } finally {
      setLoading(false);
    }

    if (mockSid && mockInput) {
      onComplete(mockSid, mockInput, mockTitle || undefined);
    }
  };

  return (
    <div className="phase1-input">
      <div className="phase-card">
        <h2 className="phase-title">
          <span className="phase-title-step">
            <EditableLabel labelKey="phase1.stepLabel" defaultValue="Step1:" />
          </span>
          <span className="phase-title-text">
            <EditableLabel labelKey="phase1.title" defaultValue="分野の指定" />
          </span>
        </h2>
        <div className="workflow-step-description phase-description-banner">
          <EditableLabel
            labelKey="phase1.description"
            defaultValue="特許マップの分野を指定するため、特許例の入力（PDF型式、5~10件程度）を完了してください"
          />
        </div>
        <form onSubmit={handleSubmit}>
          {/* 利用分野 */}
          <div className="input-section">
            <label htmlFor="usage-field">
              <span className="label-text">
                <EditableLabel
                  labelKey="phase1.field1Label"
                  defaultValue="1. 利用分野（Application Field）"
                />
              </span>
              <span className="required">
                <EditableLabel labelKey="common.required" defaultValue="*" />
              </span>
            </label>
            <p className="input-hint">
              <EditableLabel
                labelKey="phase1.field1Hint"
                defaultValue="特許マップの対象となる技術が使用されている製品分野を記入してください"
              />
            </p>
            <EditablePlaceholder
              labelKey="phase1.field1Placeholder"
              defaultValue={labels.phase1.field1Placeholder}
            >
              {(ph) => (
                <textarea
                  id="usage-field"
                  value={usageField}
                  onChange={(e) => {
                    setUsageField(e.target.value);
                    handleFieldChange();
                  }}
                  placeholder={ph}
                  rows={3}
                  disabled={loading}
                  className={error ? 'error' : ''}
                />
              )}
            </EditablePlaceholder>
          </div>

          {/* 技術的特徴 */}
          <div className="input-section">
            <label htmlFor="technical-feature">
              <span className="label-text">
                <EditableLabel
                  labelKey="phase1.field2Label"
                  defaultValue="2. 技術的特徴（Technical Features）"
                />
              </span>
              <span className="required">
                <EditableLabel labelKey="common.required" defaultValue="*" />
              </span>
            </label>
            <p className="input-hint">
              <EditableLabel
                labelKey="phase1.field2Hint"
                defaultValue="特許マップの対象となる技術について、技術の概要（特徴を含む）を記入してください"
              />
            </p>
            <EditablePlaceholder
              labelKey="phase1.field2Placeholder"
              defaultValue={labels.phase1.field2Placeholder}
            >
              {(ph) => (
                <textarea
                  id="technical-feature"
                  value={technicalFeature}
                  onChange={(e) => {
                    setTechnicalFeature(e.target.value);
                    handleFieldChange();
                  }}
                  placeholder={ph}
                  rows={3}
                  disabled={loading}
                  className={error ? 'error' : ''}
                />
              )}
            </EditablePlaceholder>
          </div>

          {/* 課題 */}
          <div className="input-section">
            <label htmlFor="problem">
              <span className="label-text">
                <EditableLabel
                  labelKey="phase1.field3Label"
                  defaultValue="3. 課題（Problem/Challenge）"
                />
              </span>
              <span className="required">
                <EditableLabel labelKey="common.required" defaultValue="*" />
              </span>
            </label>
            <p className="input-hint">
              <EditableLabel
                labelKey="phase1.field3Hint"
                defaultValue="特許マップの対象となる技術に関して、解決しようとしている課題を記入してください"
              />
            </p>
            <EditablePlaceholder
              labelKey="phase1.field3Placeholder"
              defaultValue={labels.phase1.field3Placeholder}
            >
              {(ph) => (
                <textarea
                  id="problem"
                  value={problem}
                  onChange={(e) => {
                    setProblem(e.target.value);
                    handleFieldChange();
                  }}
                  placeholder={ph}
                  rows={3}
                  disabled={loading}
                  className={error ? 'error' : ''}
                />
              )}
            </EditablePlaceholder>
          </div>

          {/* 効果作用 */}
          <div className="input-section">
            <label htmlFor="effect">
              <span className="label-text">
                <EditableLabel
                  labelKey="phase1.field4Label"
                  defaultValue="4. 効果作用（Effect/Impact）"
                />
              </span>
              <span className="required">
                <EditableLabel labelKey="common.required" defaultValue="*" />
              </span>
            </label>
            <p className="input-hint">
              <EditableLabel
                labelKey="phase1.field4Hint"
                defaultValue="特許マップの対象となる技術に関して、期待する効果（得たい結果）を記入してください"
              />
            </p>
            <EditablePlaceholder
              labelKey="phase1.field4Placeholder"
              defaultValue={labels.phase1.field4Placeholder}
            >
              {(ph) => (
                <textarea
                  id="effect"
                  value={effect}
                  onChange={(e) => {
                    setEffect(e.target.value);
                    handleFieldChange();
                  }}
                  placeholder={ph}
                  rows={3}
                  disabled={loading}
                  className={error ? 'error' : ''}
                />
              )}
            </EditablePlaceholder>
          </div>

          {/* 文字数カウンター */}
          <div className="char-counter-total">
            <span className={`char-counter ${charCount > 2000 ? 'error' : ''}`}>
              <EditableLabel labelKey="phase1.charCountLabel" defaultValue="合計:" /> {charCount} /
              2000
              <EditableLabel labelKey="phase1.charCountUnit" defaultValue="文字" />
            </span>
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="submit-btn-wrapper">
            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? (
                <>
                  <span className="spinner"></span>
                  <EditableLabel labelKey="phase1.submitLoading" defaultValue="検証中..." />
                </>
              ) : (
                <EditableLabel labelKey="phase1.submitButton" defaultValue="次へ" />
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Phase1Input;
